import React from 'react';
import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { LocationOn as LocationIcon, KeyboardArrowDown as ArrowIcon } from '@mui/icons-material';
import { useGetMyProfileQuery } from '../../../services/ProfileService';
import { fcColors } from '../../../styles/theme';

interface CenterSelectProps {
  value: string;
  onChange: (code: string) => void;
}

/**
 * Center picker for the claims queue (spec decision #12): center-grouped like Financial Clearance,
 * with **no "All Centers" option** — the C1 UI always scopes to a single hospital center. Options
 * are restricted to the signed-in user's provisioned `centerAccess` (CAM Edit is only granted for
 * the CAM-enabled hospital centers, so no client-side facility-type filter is needed here).
 * Fully controlled — default selection is owned by `ClaimsQueuePage` (seeded from the app-wide
 * center), not here.
 */
const CenterSelect: React.FC<CenterSelectProps> = ({ value, onChange }) => {
  const { data: profile } = useGetMyProfileQuery();
  const centers = profile?.centerAccess?.map((c) => ({ code: c.code, name: c.name })) ?? [];

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: 3,
        px: 2,
        py: 0.75,
        border: `2px solid ${fcColors.border}`,
        minWidth: 220,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: fcColors.muted,
          flexShrink: 0,
        }}
      >
        <LocationIcon sx={{ color: '#ffffff', fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: fcColors.muted,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 1,
            fontSize: '0.65rem',
            lineHeight: 1,
            display: 'block',
          }}
        >
          Center
        </Typography>
        <Select
          size="small"
          value={centers.find((c) => c.code === value) ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          IconComponent={ArrowIcon}
          variant="standard"
          disableUnderline
          displayEmpty
          data-testid="claims-center-select"
          renderValue={(val) => {
            const c = centers.find((x) => x.code === val);
            return (
              <Typography variant="body1" sx={{ fontWeight: 700, color: fcColors.dark, fontSize: '0.95rem' }}>
                {c?.name ?? 'Choose a center...'}
              </Typography>
            );
          }}
          sx={{
            width: '100%',
            '& .MuiSelect-select': { py: 0, pr: '28px !important' },
            '& .MuiSvgIcon-root': { color: fcColors.muted, fontSize: 22 },
            '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
          }}
        >
          {centers.map((c) => (
            <MenuItem key={c.code} value={c.code} data-testid={`claims-center-option-${c.code}`}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Stack>
  );
};

export default CenterSelect;
